import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import {
  hsnCodeService,
  UpdateHSNCodeDto,
} from "../../services/hsn-code.service";
import { showToast } from "../../lib/toast";

export function EditHSNCodePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<UpdateHSNCodeDto>({
    hsnCode: "",
    hsnDescription: "",
    gstRate: 12,
    cgstRate: undefined,
    sgstRate: undefined,
    igstRate: undefined,
    cessRate: 0,
    chapter: "",
    heading: "",
    subheading: "",
    isActive: true,
  });

  useEffect(() => {
    if (id) {
      loadHSNCode();
    }
  }, [id]);

  const loadHSNCode = async () => {
    try {
      setLoading(true);
      const data = await hsnCodeService.getById(id!);
      setFormData({
        hsnCode: data.hsnCode,
        hsnDescription: data.hsnDescription,
        gstRate: data.gstRate,
        cgstRate: data.cgstRate,
        sgstRate: data.sgstRate,
        igstRate: data.igstRate,
        cessRate: data.cessRate || 0,
        chapter: data.chapter || "",
        heading: data.heading || "",
        subheading: data.subheading || "",
        isActive: data.isActive,
      });
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to load HSN code");
      navigate("/hsn-codes");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const target = e.target;
    const { name, value, type } = target;
    const checked = (target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? value === ""
              ? undefined
              : parseFloat(value)
            : value,
    }));
  };

  const handleGstRateChange = (rate: number) => {
    setFormData((prev) => ({
      ...prev,
      gstRate: rate,
      cgstRate: rate / 2,
      sgstRate: rate / 2,
      igstRate: rate,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.hsnCode || !formData.hsnDescription) {
      showToast.error("HSN code and description are required");
      return;
    }

    try {
      setSaving(true);
      await hsnCodeService.update(id!, formData);
      showToast.success("HSN code updated successfully");
      navigate("/hsn-codes");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to update HSN code",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/hsn-codes")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to HSN Codes
        </button>

        <h1 className="text-2xl font-bold text-gray-900">Edit HSN Code</h1>
        <p className="text-sm text-gray-500 mt-1">
          Update HSN code details for GST compliance
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
        {/* HSN Details */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            HSN Details
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HSN Code *
              </label>
              <input
                type="text"
                name="hsnCode"
                value={formData.hsnCode}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                placeholder="e.g., 30049099"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chapter
              </label>
              <input
                type="text"
                name="chapter"
                value={formData.chapter}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 30"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                name="hsnDescription"
                value={formData.hsnDescription}
                onChange={handleChange}
                required
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Medicaments consisting of mixed or unmixed products..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heading
              </label>
              <input
                type="text"
                name="heading"
                value={formData.heading}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 3004"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subheading
              </label>
              <input
                type="text"
                name="subheading"
                value={formData.subheading}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., 300490"
              />
            </div>
          </div>
        </div>

        {/* GST Rates */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            GST Rates
          </h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              GST Slab
            </label>
            <div className="flex flex-wrap gap-2">
              {[0, 5, 12, 18, 28].map((rate) => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => handleGstRateChange(rate)}
                  className={`px-4 py-2 rounded-lg border transition ${
                    formData.gstRate === rate
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-blue-500"
                  }`}
                >
                  {rate}%
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CGST Rate (%)
              </label>
              <input
                type="number"
                name="cgstRate"
                value={formData.cgstRate ?? (formData.gstRate ?? 0) / 2}
                onChange={handleChange}
                step="0.01"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SGST Rate (%)
              </label>
              <input
                type="number"
                name="sgstRate"
                value={formData.sgstRate ?? (formData.gstRate ?? 0) / 2}
                onChange={handleChange}
                step="0.01"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IGST Rate (%)
              </label>
              <input
                type="number"
                name="igstRate"
                value={formData.igstRate ?? formData.gstRate}
                onChange={handleChange}
                step="0.01"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cess Rate (%)
              </label>
              <input
                type="number"
                name="cessRate"
                value={formData.cessRate}
                onChange={handleChange}
                step="0.01"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="mb-8">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Active</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate("/hsn-codes")}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditHSNCodePage;
